/* eslint-disable max-lines */
import {
    EmbedEvent,
    EmbedEvents,
    EmbedNotificationType,
    InternalEvents,
} from './types/events'
import {
    Environment, resolveInstanceEnvironment,
} from '../appconfig'
import {
    NinchatCallbacks,
    NinchatEnvironmentConfig,
    NinchatEnvironmentConfigTheme,
    NinchatEventCallback,
    NinchatOptions,
} from './types'

import {
    attachEvents, floatingContainerComponentTemplate, handleChildEvents, render,
} from './components/embedWrapper'
import audioManager, {AudioEvents} from 'utils/audio'

import {
    isVersionedVariant, selectDeploymentVariant,
} from '../appconfig/versionedDeployment'

import {ContainerNotFoundErrorEvent} from './types/errorEvents'
import {EmbedErrorType} from './types/errorTypes'
import {ResponsiveBreakpoints} from './types/responsive'
import {Route} from '../navigation/types'
import {RouteName} from './types/routes'
import {ThemeManager} from './helpers/themeManager'

import {buildConfig} from './utils'
import {globalStyles} from './styles/global'
import {
    iframe as iframeStyles,
} from './styles'
import {merge} from 'lodash'

const resolveOptionsEnvironment = async (options: NinchatOptions) => {
    const mode = options.mode || process.env.MODE as Environment
    const variant = await selectDeploymentVariant(options.variant, mode)
    return resolveInstanceEnvironment({
        mode: options.mode,
        variant,
    })
}

export class NinchatEmbed {
    public ENVIRONMENT = resolveInstanceEnvironment()

    private get_EMBED_HOST = () => this.ENVIRONMENT.staticEmbedURL

    private get_EMBED_ORIGIN = () => this.get_EMBED_HOST() && new URL(this.get_EMBED_HOST() || '').origin

    private get_ENTRY_POINT = () => (this.get_EMBED_HOST()?.includes('localhost') || this.ENVIRONMENT.isNative ? 'embed3.html' : 'embed3')

    public readonly Event = EmbedEvent

    public readonly ErrorType = EmbedErrorType

    public readonly NotificationType = EmbedNotificationType

    public readonly RouteName = RouteName

    private callbacks = <NinchatCallbacks>{}

    private iframe: HTMLIFrameElement | undefined = undefined

    private container: HTMLDivElement | undefined = undefined

    private config: NinchatEnvironmentConfig | undefined = undefined

    private currentBreakpoint = 'default'

    private currentBreakpointConfig: NinchatEnvironmentConfig | undefined = undefined

    private options: NinchatOptions | undefined = undefined

    private breakpoints: ResponsiveBreakpoints = []

    private isWebView = false


    private emit(event: Partial<EmbedEvents> | {event: 'all_events'}) {
        if (event.event && this.callbacks[event.event]) {
            this.callbacks[event.event].forEach((callback) => {
                if (InternalEvents.includes(event.event as EmbedEvent)) {
                    // Internal events are not emitted to public callbacks
                    return
                }

                callback(event)
            })
        }

        // only for webview, emit all events to all_events callback
        // eslint-disable-next-line dot-notation
        if (this.callbacks['all_events'] && this.isWebView) {
            // eslint-disable-next-line dot-notation
            this.callbacks['all_events'].forEach((callback) => {
                callback(event)
            })
        }
    }

    private sendMessage(message: Partial<EmbedEvents>) {
        if (this.iframe && this.get_EMBED_ORIGIN()) {
            this.iframe.contentWindow?.postMessage(message, this.get_EMBED_ORIGIN() || '')
        }
    }

    private onMessage = (event: MessageEvent) => {
        if (event.origin === this.get_EMBED_ORIGIN()) {
            try {
                const eventsData = JSON.parse(event.data)
                const configSessionKey = this.config?.sessionKey || ''

                // only allow messages with the same sessionKey
                if (eventsData.sessionKey === configSessionKey) {
                    handleChildEvents(
                        event,
                        this.container as HTMLElement,
                        this.currentBreakpointConfig as NinchatEnvironmentConfig,
                        this.sendMessage.bind(this),
                        this.isWebView,
                        this.emit.bind(this),
                    )
                    this.emit(eventsData)
                }
            // eslint-disable-next-line no-empty
            } catch (error) {}
        }
    }

    private getCurrentBreakpointConfig = (defaultConfig: NinchatEnvironmentConfig) => {
        const selectedBreakpoint = this.breakpoints.find(({mediaQuery}) => window.matchMedia(mediaQuery).matches)

        if (selectedBreakpoint) {
            const {config: newConfig} = selectedBreakpoint
            return newConfig
        }
        return defaultConfig
    }

    // eslint-disable-next-line class-methods-use-this
    private generateResponsiveBreakpoints = (config: NinchatEnvironmentConfig) => {
        const {responsive} = config

        return Promise.all((responsive || []).map(async (resp) => {
            const minValue = typeof resp?.minWidth === 'undefined' ? '' : `(min-width: ${resp.minWidth}px)`
            const maxValue = typeof resp?.maxWidth === 'undefined' ? '' : `(max-width: ${resp.maxWidth}px)`

            let mediaQuery = ''
            if (minValue && maxValue) {
                mediaQuery = `${minValue} and ${maxValue}`
            } else if (minValue) {
                mediaQuery = minValue
            } else if (maxValue) {
                mediaQuery = maxValue
            }

            const newConfig = merge({}, config, resp?.overrides)

            const thememanager = new ThemeManager()
            await thememanager.buildTheme(newConfig?.colorScheme, newConfig.theme as NinchatEnvironmentConfigTheme, this.isWebView)

            return {
                config: newConfig,
                mediaQuery,
                tm: thememanager,
            }
        }))
    }

    // eslint-disable-next-line max-params, class-methods-use-this
    private handleResponsive = (defaultConfig: NinchatEnvironmentConfig, floatingContainer: HTMLElement, emit: (event: Partial<EmbedEvents>) => void) => {
        const renderCurrentBreakpoint = () => {
            const selectedBreakpoint = this.breakpoints.find(({mediaQuery}) => window.matchMedia(mediaQuery).matches)

            if (selectedBreakpoint && this.currentBreakpoint !== selectedBreakpoint.mediaQuery) {
                const {
                    mediaQuery, config: newConfig,
                } = selectedBreakpoint
                this.currentBreakpoint = mediaQuery

                render(newConfig, floatingContainer, emit)
            } else if (!selectedBreakpoint && this.currentBreakpoint !== 'default') {
                this.currentBreakpoint = 'default'

                render(defaultConfig, floatingContainer, this.emit.bind(this))
            }
        }

        window.addEventListener('resize', () => {
            renderCurrentBreakpoint()
        })
    }

    private buildConfig = async (options: NinchatOptions) => {
        const {
            config,
            configKey,
            configUrls,
            environment,
            postConfigUrls,
        } = options

        const buildConfigParams = {
            appEnvironment: this.ENVIRONMENT,
            config,
            configKey,
            configUrls,
            environment,
            postConfigUrls,
        }
        try {
            const newConfig = await buildConfig(buildConfigParams, this.get_EMBED_ORIGIN() || '')
            return newConfig
        } catch (error) {
            return error
        }
    }

    private buildContainer = async (options: NinchatOptions, config: NinchatEnvironmentConfig) => {
        const {
            containerId, version, siteSecret, isWebView,
        } = options

        const floating = config.floating || false

        const {origin} = window.location
        const urlParams = new URLSearchParams(window.location.search)

        // override default sounds with user provided sounds
        Object.keys(config.sounds || {}).forEach((event) => {
            if (event in audioManager.soundEffects) {
                const soundFile = config.sounds?.[event as AudioEvents]
                if (soundFile !== undefined && soundFile !== true) {
                    audioManager.overrideSoundEffect(event as AudioEvents, soundFile)
                }
            }
        })


        // Params that are NOT sent to server via query (placed after #)
        // TODO: validate via Zod
        const searchParams = new URLSearchParams()

        Object.keys(options).forEach((key) => {
            // containerId is not sent to embed
            // eslint-disable-next-line max-statements-per-line
            if (key === 'containerId') { return }
            searchParams.set(key, JSON.stringify(options[key]))
        })

        const iframeUrl = new URL(`${this.get_EMBED_HOST()}/${this.get_ENTRY_POINT()}`)

        iframeUrl.searchParams.set('targetOrigin', origin)
        // version is taken from Embed init function, library client decide on its value
        // this.ENVIRONMENT.xUserAgent is taken from embed library package.json version
        searchParams.set('xUserAgent', JSON.stringify(`${version ? `${version} ` : ''}${this.ENVIRONMENT.xUserAgent}`))

        if (siteSecret) {
            searchParams.set('siteSecret', JSON.stringify(siteSecret))
        }

        if (isWebView) {
            searchParams.set('isWebView', JSON.stringify(isWebView))
            this.isWebView = true
        }

        searchParams.set('parentUrl', JSON.stringify(window.location.href))

        const ssoAccessKey = urlParams.get('sso_access_key')
        if (ssoAccessKey) {
            searchParams.set('ssoAccessKey', JSON.stringify(ssoAccessKey))
            const cleanUrl = new URL(window.location.href)
            cleanUrl.searchParams.delete('sso_access_key')
            window.history.replaceState({}, document.title, cleanUrl.toString())
        }

        if (window.location.hash && window.location.hash.includes('/x/sso/')) {
            // The sso route stays in parentUrl and out of the iframe src, so the persisted
            // src stays clean and a later reload does not reuse the one-time access key.
            const cleanUrl = new URL(window.location.href)
            cleanUrl.hash = ''
            window.history.replaceState({}, document.title, cleanUrl.toString())
        }

        // Params that ARE sent to server via query
        const container: HTMLDivElement | null = document.querySelector(`#${containerId}`)

        const iframe = document.createElement('iframe')
        iframe.name = `ninchat?${searchParams.toString()}`
        iframe.title = 'Ninchat Embed'
        iframe.allow = 'camera;microphone;clipboard-write;display-capture;fullscreen'
        iframe.src = iframeUrl.toString()
        iframe.className = 'embed-iframe'

        if (floating) {
            iframe.className = `embed-iframe ${iframeStyles()}`
            globalStyles(config.theme?.customFonts || [])

            if (container) {
                container.remove()
            }
            const floatingContainer = document.createElement('div')
            if (containerId) {
                floatingContainer.id = containerId
            }

            this.breakpoints = await this.generateResponsiveBreakpoints(config)
            this.currentBreakpointConfig = this.getCurrentBreakpointConfig(config)

            const thememanager = new ThemeManager()
            await thememanager.buildTheme(config?.colorScheme, config.theme as NinchatEnvironmentConfigTheme, this.isWebView)

            // template is taking default config and all breakpoints to generate all needed media queries in css
            floatingContainer.innerHTML = floatingContainerComponentTemplate({
                breakpoints: this.breakpoints,
                config,
                content: iframe.outerHTML,
                tm: thememanager,
            })
            document.body.appendChild(floatingContainer)

            this.container = floatingContainer
            const embedIframe = floatingContainer.querySelector('iframe')
            if (embedIframe) {
                this.iframe = embedIframe
            }
            attachEvents(this.sendMessage.bind(this), this.container, this.currentBreakpointConfig, this.emit.bind(this))
            this.handleResponsive(config, floatingContainer, this.emit.bind(this))
        } else if (container) {
            // for container just pass default config
            this.currentBreakpointConfig = config
            this.container = container
            container.className = 'ninchat-embed'
            iframe.style.setProperty('border', 'none')
            iframe.style.setProperty('height', '100%')
            iframe.style.setProperty('width', '100%')
            container.appendChild(iframe)
            this.iframe = iframe
        } else {
            const errorEvent: ContainerNotFoundErrorEvent = {
                container_id: containerId,
                error_type: EmbedErrorType.ContainerNotFoundError,
                event: EmbedEvent.Error,
                targetOrigin: origin,
            }

            this.emit(errorEvent)
        }

        if (floating || container) {
            // trigger after initialization
            this.emit({event: EmbedEvent.ContainerInitialized})
        }
    }

    public destruct() {
        window.removeEventListener('message', this.onMessage)
        this.callbacks = <NinchatCallbacks>{}
    }

    public off(event: EmbedEvent, callback: NinchatEventCallback) {
        if (this.callbacks[event]) {
            for (let i = 0; i < this.callbacks[event].length; i += 1) {
                if (this.callbacks[event][i] === callback) {
                    this.callbacks[event].splice(i, 1)
                    break
                }
            }
        }
    }

    public on(event: EmbedEvent, callback: NinchatEventCallback) {
        this.callbacks[event] ||= []
        this.callbacks[event].push(callback)
    }

    public init(options: NinchatOptions) {
        this.options = options
        this.config = undefined
        this.initialize(options)
    }

    private initialize = async (options: NinchatOptions) => {
        const selectedEnvironment = await resolveOptionsEnvironment(options)
        if (this.options !== options) {
            return
        }
        this.ENVIRONMENT = selectedEnvironment
        const config = await this.buildConfig(options) as NinchatEnvironmentConfig
        if (this.options !== options) {
            return
        }
        this.config = config
        if (config.autoStart !== false) {
            this.buildContainer(options, config)
        }
    }

    public start() {
        if (this.options && this.config) {
            this.startEmbed(this.options)
        }
    }

    private startEmbed = async (options: NinchatOptions) => {
        if (isVersionedVariant(options.variant)) {
            // A release can expire between initialization and a delayed start.
            const previousVariant = this.ENVIRONMENT.variant
            const selectedEnvironment = await resolveOptionsEnvironment(options)
            if (this.options !== options) {
                return
            }
            this.ENVIRONMENT = selectedEnvironment
            if (this.ENVIRONMENT.variant !== previousVariant) {
                const config = await this.buildConfig(options) as NinchatEnvironmentConfig
                if (this.options !== options) {
                    return
                }
                this.config = config
            }
        }
        if (this.config && this.options === options) {
            this.buildContainer(options, this.config)
        }
    }

    public route(route: Route) {
        if (this.iframe && this.get_EMBED_ORIGIN()) {
            this.iframe.contentWindow?.postMessage({route}, this.get_EMBED_ORIGIN() || '')
        }
    }

    public acceptAudience({
        queueId, audienceId,
    }: {
        queueId: string
        audienceId?: string
    }) {
        this.sendMessage({
            audience_id: audienceId,
            event: EmbedEvent.AcceptAudience,
            queue_id: queueId,
        })
    }

    public acceptServiceRequest({queueId}: {queueId: string}) {
        this.sendMessage({
            event: EmbedEvent.AcceptServiceRequest,
            queue_id: queueId,
        })
    }

    public completeServiceRequest({audienceId}: {audienceId: string}) {
        this.sendMessage({
            audience_id: audienceId,
            event: EmbedEvent.CompleteServiceRequest,
        })
    }

    public endConversation({channelId}: {channelId: string}) {
        this.sendMessage({
            channel_id: channelId,
            event: EmbedEvent.EndConversation,
        })
    }

    constructor() {
        window.addEventListener('message', this.onMessage.bind(this), false)
    }
}

export default NinchatEmbed

export {
    EmbedEvent,
    EmbedEvents,
    NinchatOptions,
}
